import { FormControlLabel, MenuItem, Radio } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import NgpCheckbox from '../../../../form/NgpCheckbox';
import NgpRadioGroup from '../../../../form/NgpRadioGroup';
import NgpSelect from '../../../../form/NgpSelect';
import { DAILY, INTERVAL } from '../../ServiceTestsScheduleConstants';

const useStyles = makeStyles((theme) => ({
    radioWrapper: {
        margin: '0px 0',

        '& .MuiFormControlLabel-root': {
            marginTop: 0,
        },
    },

    rowSpacing: {
        marginBottom: theme.spacing(0),
    },
    windowRow: {
        paddingLeft: theme.spacing(5), // indent Between row to distinguish from radio rows
    },
    rowLabel: {
        minWidth: 180,
        height: theme.spacing(4),
        margin: 0,
        flexShrink: 0,
    },
    rowWindowLabel: {
        minWidth: 140,
        height: theme.spacing(4),
        margin: 0,
        flexShrink: 0,
    },
    rowField: {
        minWidth: 200,
        flexShrink: 0,
    },
    scheduleRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end',
        '& .MuiTextField-root': {
            marginRight: theme.spacing(3),
        },
        height: theme.spacing(7),
    },
}));

/**
 * Reusable schedule form section — renders the Interval / Window / Daily controls.
 * Designed to be embedded in a Dialog (via ServiceTestsScheduleDialog) or any other form context.
 * Requires a react-hook-form FormProvider ancestor.
 */
export default function ScheduleFields({ intervals, times, tzAbbr }) {
    const classes = useStyles();
    const { i18n } = useLingui();
    const { watch, setValue, clearErrors } = useFormContext();

    const scheduleType = watch('scheduleType');
    const isWindow = watch('isWindow');

    const isIntervalMode = scheduleType === INTERVAL;
    const isDailyMode = scheduleType === DAILY;

    // Track previous isWindow to distinguish "user just checked Between" from
    // "scheduleType changed while Between was already checked". Without this,
    // both effects run in the same render cycle when the user clicks "Run daily at"
    // while Between is checked: effect #1 sets isWindow=false, but effect #2 still
    // sees the old isWindow=true and reverts scheduleType back to INTERVAL.
    const prevIsWindowRef = useRef(isWindow);

    // When switching to DAILY: clear all interval-mode fields
    useEffect(() => {
        if (scheduleType === DAILY) {
            setValue(INTERVAL, '');
            setValue('isWindow', false);
            setValue('windowStart', '');
            setValue('windowEnd', '');
            clearErrors([INTERVAL, 'isWindow', 'windowStart', 'windowEnd']);
        } else {
            setValue('dailyStart', '');
            clearErrors('dailyStart');
        }
    }, [scheduleType, setValue, clearErrors]);

    // Checking "Between" while in DAILY mode switches to interval+window mode.
    // Unchecking clears the window time fields.
    // Only switch scheduleType when isWindow genuinely transitions false → true
    // (not when scheduleType changed while isWindow was already true).
    useEffect(() => {
        const wasWindow = prevIsWindowRef.current;
        prevIsWindowRef.current = isWindow;

        if (isWindow && !wasWindow) {
            // User just checked "Between" — if in DAILY mode, switch to INTERVAL
            if (scheduleType === DAILY) {
                setValue('scheduleType', INTERVAL);
            }
        } else if (!isWindow) {
            setValue('windowStart', '');
            setValue('windowEnd', '');
            clearErrors(['windowStart', 'windowEnd']);
        }
    }, [isWindow, scheduleType, setValue, clearErrors]);

    return (
        <NgpRadioGroup
            className={classes.radioWrapper}
            fieldName="scheduleType"
            valueType="string"
            aria-label={i18n._(t`Run schedule type`)}
        >
            {/* ── INTERVAL ROW ────────────────────────────────────────────── */}
            <div className={`${classes.scheduleRow} ${classes.rowSpacing}`}>
                <FormControlLabel
                    value={INTERVAL}
                    control={<Radio />}
                    label={<Trans>Run test every</Trans>}
                    className={classes.rowLabel}
                />
                <div className={classes.rowField}>
                    <NgpSelect
                        fieldName="interval"
                        label={<Trans>Interval</Trans>}
                        disabled={!isIntervalMode}
                        fullWidth
                    >
                        {intervals.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.text}
                            </MenuItem>
                        ))}
                    </NgpSelect>
                </div>
            </div>

            {/* ── WINDOW ROW (indented, interval sub-option) ──────────────── */}
            <div className={`${classes.scheduleRow} ${classes.rowSpacing} ${classes.windowRow}`}>
                <div className={classes.rowWindowLabel}>
                    <NgpCheckbox fieldName="isWindow" label={<Trans>Between</Trans>} />
                </div>
                <div className={classes.rowField}>
                    <NgpSelect
                        fieldName="windowStart"
                        label={`${i18n._(t`Start Time`)} (${tzAbbr})`}
                        disabled={!isWindow}
                        fullWidth
                    >
                        {times.map((time) => (
                            <MenuItem key={time.value} value={time.value}>
                                {time.text}
                            </MenuItem>
                        ))}
                    </NgpSelect>
                </div>
                <div className={classes.rowField}>
                    <NgpSelect
                        fieldName="windowEnd"
                        label={`${i18n._(t`End Time`)} (${tzAbbr})`}
                        disabled={!isWindow}
                        fullWidth
                    >
                        {times.map((time) => (
                            <MenuItem key={time.value} value={time.value}>
                                {time.text}
                            </MenuItem>
                        ))}
                    </NgpSelect>
                </div>
            </div>

            {/* ── DAILY ROW ───────────────────────────────────────────────── */}
            <div className={`${classes.scheduleRow} ${classes.rowSpacing}`}>
                <FormControlLabel
                    value={DAILY}
                    control={<Radio />}
                    label={<Trans>Run test daily at</Trans>}
                    className={classes.rowLabel}
                />
                <div className={classes.rowField}>
                    <NgpSelect
                        fieldName="dailyStart"
                        label={`${i18n._(t`Start Time`)} (${tzAbbr})`}
                        disabled={!isDailyMode}
                        fullWidth
                    >
                        {times.map((time) => (
                            <MenuItem key={time.value} value={time.value}>
                                {time.text}
                            </MenuItem>
                        ))}
                    </NgpSelect>
                </div>
            </div>
        </NgpRadioGroup>
    );
}

ScheduleFields.propTypes = {
    intervals: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.number,
            text: PropTypes.string,
        })
    ).isRequired,
    times: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string,
            text: PropTypes.string,
        })
    ).isRequired,
    tzAbbr: PropTypes.string.isRequired,
};
